import { RVFormItem, RVCheckbox, RVCheckboxProps } from "../../atoms";

import type { FormItemProps } from "antd";

export type RVFormCheckboxProps = FormItemProps & {
    /** Checkbox label content */
    children?: React.ReactNode;
    /** Props passed directly to the RVCheckbox component */
    checkboxProps?: Omit<RVCheckboxProps, "children">;
};

/**
 * RVFormCheckbox
 *
 * A molecule component that combines RVCheckbox with Ant Design's Form.Item for seamless form integration.
 *
 * - Automatically sets valuePropName="checked" for proper form binding
 * - Supports all Form.Item props (label, rules, tooltip, etc.)
 * - Passes through checkbox-specific props to RVCheckbox
 * - Simplifies checkbox usage in forms by handling common patterns
 *
 * @example
 * // Basic usage in a form
 * <Form>
 *   <RVFormCheckbox name="acceptTerms">
 *     I accept the terms and conditions
 *   </RVFormCheckbox>
 * </Form>
 *
 * @example
 * // With validation
 * <RVFormCheckbox
 *   name="consent"
 *   rules={[{ required: true, message: 'You must accept to continue' }]}
 * >
 *   I consent to data processing
 * </RVFormCheckbox>
 *
 * @example
 * // With Form.Item label (separate from checkbox label)
 * <RVFormCheckbox
 *   name="isDefault"
 *   label="Default Settings"
 *   tooltip="Check this to make it the default"
 * >
 *   Set as default
 * </RVFormCheckbox>
 *
 * @param {RVFormCheckboxProps} props - Component props
 * @returns {JSX.Element}
 */
export function RVFormCheckbox({ checkboxProps, children, ...rest }: RVFormCheckboxProps): JSX.Element {
    return (
        <RVFormItem {...rest} valuePropName="checked">
            <RVCheckbox {...checkboxProps}>{children}</RVCheckbox>
        </RVFormItem>
    );
}
